// @ts-nocheck -- staged migration: add explicit types before removing this directive.
import { fetchAuthSession } from "aws-amplify/auth";
import { notifyProgressSaved } from "./progressEvents";

const TABLE_NAME = process.env.REACT_APP_DYNAMODB_TABLE_NAME;

// The DynamoDB SDK is only needed once a signed-in user's progress actually
// gets read from or written to the cloud - not for every anonymous visitor,
// who never calls getCloudProgress/putCloudProgress at all. Loading it
// dynamically keeps it out of the eager main bundle.
let modulesPromise = null;
let testModulesLoader = null;

// Keeps the SDK lazy in production while allowing tests to reset only this
// module's loader cache instead of resetting Jest's entire module registry.
export function __resetProgressCloudModulesForTests() {
    if (process.env.NODE_ENV === "test") modulesPromise = null;
}

export function __setProgressCloudModulesLoaderForTests(loader) {
    if (process.env.NODE_ENV === "test") {
        modulesPromise = null;
        testModulesLoader = loader;
    }
}

function loadModules() {
    if (!modulesPromise) {
        modulesPromise = (testModulesLoader ? Promise.resolve().then(testModulesLoader) : Promise.all([
            import("@aws-sdk/client-dynamodb"),
            import("@aws-sdk/lib-dynamodb"),
        ])
            .then(([{ DynamoDBClient }, { DynamoDBDocumentClient, GetCommand, PutCommand, BatchGetCommand }]) => {
                const client = new DynamoDBClient({
                    region: process.env.REACT_APP_AWS_REGION,
                    credentials: async () => {
                        const session = await fetchAuthSession();
                        return session.credentials;
                    },
                });
                return { docClient: DynamoDBDocumentClient.from(client), GetCommand, PutCommand, BatchGetCommand };
            }))
            .catch((err) => {
                // Don't let a transient failure (e.g. a network blip loading the SDK
                // chunks) permanently break cloud sync for the rest of the session -
                // let the next call retry from scratch.
                modulesPromise = null;
                throw err;
            });
    }
    return modulesPromise;
}

export async function getCloudProgress(userId, feature) {
    const { docClient, GetCommand } = await loadModules();
    const { Item } = await docClient.send(
        new GetCommand({ TableName: TABLE_NAME, Key: { userId, feature } })
    );
    return Item ?? null;
}

// Reads several features for one user in a single round trip, rather than
// N sequential getCloudProgress calls - used by the levels/XP aggregation,
// which needs about ten feature rows just to render a header badge.
// Incomplete responses must not masquerade as a lower level. The caller
// displays an error and offers a retry if DynamoDB leaves keys unprocessed.
export async function getCloudProgressBatch(userId, features) {
    if (!features?.length) return {};
    const { docClient, BatchGetCommand } = await loadModules();
    const { Responses, UnprocessedKeys } = await docClient.send(
        new BatchGetCommand({
            RequestItems: {
                [TABLE_NAME]: { Keys: features.map((feature) => ({ userId, feature })), ConsistentRead: true },
            },
        })
    );
    if (UnprocessedKeys?.[TABLE_NAME]?.Keys?.length) {
        throw new Error("Progress read incomplete; please retry.");
    }
    const items = Responses?.[TABLE_NAME] ?? [];
    const byFeature: Record<string, { data: any }> = {};
    items.forEach((item) => {
        byFeature[item.feature] = item;
    });
    return byFeature;
}

export async function putCloudProgress(userId, feature, data) {
    const { docClient, PutCommand } = await loadModules();
    await docClient.send(
        new PutCommand({
            TableName: TABLE_NAME,
            Item: { userId, feature, data, updatedAt: new Date().toISOString() },
        })
    );
    notifyProgressSaved({ userId, feature });
}
